import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/authContext';
import {
  getSsclRate,
  publishSsclRate,
  getExchangeRateHistory,
  getExchangeRateFetchLog,
  getInvoiceBankCharge,
  publishInvoiceBankCharge,
} from '../../api/exchangeRate';
import { formatRate } from '../../utils/exchangeRates';

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** SSCL tax / exchange-rate payment overhaul (Phase 79) — the one Master
 * Data page for both finance-config concerns: publishing a new SSCL%
 * (append-only history, same pattern explained in the panel below) and
 * viewing the exchange-rate history/fetch log the external fetcher has
 * sent (see scripts/exchange-rate-fetcher) — read-only here, since the
 * actual fetch runs on a separate machine, not triggerable from this app.
 */
export const SsclSettingsPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('master_data', 'update');
  const queryClient = useQueryClient();
  const [percentage, setPercentage] = useState('');
  const [calculationDivisor, setCalculationDivisor] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ssclQuery = useQuery({ queryKey: ['sscl-rate'], queryFn: getSsclRate });
  const rateHistoryQuery = useQuery({ queryKey: ['exchange-rate-history'], queryFn: getExchangeRateHistory });
  const fetchLogQuery = useQuery({ queryKey: ['exchange-rate-log'], queryFn: getExchangeRateFetchLog });

  // Formatted USD/LKR Invoice — the "Bank Charges" figure added to a USD
  // invoice's TOTAL (never shown/added on an LKR invoice).
  const [bankChargeAmount, setBankChargeAmount] = useState('');
  const [bankChargeError, setBankChargeError] = useState<string | null>(null);
  const bankChargeQuery = useQuery({ queryKey: ['invoice-bank-charge'], queryFn: getInvoiceBankCharge });

  useEffect(() => {
    if (bankChargeQuery.data?.current && bankChargeAmount === '') {
      setBankChargeAmount(Number(bankChargeQuery.data.current.amountUsd).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankChargeQuery.data?.current]);

  const publishBankChargeMutation = useMutation({
    mutationFn: (amountUsd: number) => publishInvoiceBankCharge(amountUsd),
    onSuccess: () => {
      setBankChargeError(null);
      queryClient.invalidateQueries({ queryKey: ['invoice-bank-charge'] });
    },
    onError: () => setBankChargeError('Could not publish the new Bank Charges amount. Please try again.'),
  });

  const handlePublishBankCharge = () => {
    const value = Number(bankChargeAmount);
    if (!Number.isFinite(value) || value < 0) {
      setBankChargeError('Enter an amount of 0 or more.');
      return;
    }
    publishBankChargeMutation.mutate(value);
  };

  // SSCL divisor-based calculation correction — pre-fill both fields from
  // the current published row the first time it loads, so publishing an
  // update to just one of the two doesn't require re-typing the other.
  useEffect(() => {
    if (ssclQuery.data?.current && percentage === '' && calculationDivisor === '') {
      setPercentage(Number(ssclQuery.data.current.percentage).toString());
      setCalculationDivisor(Number(ssclQuery.data.current.calculationDivisor).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssclQuery.data?.current]);

  const publishMutation = useMutation({
    mutationFn: ({ percentage: p, calculationDivisor: d }: { percentage: number; calculationDivisor: number }) =>
      publishSsclRate(p, d),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['sscl-rate'] });
    },
    onError: () => setError('Could not publish the new SSCL rate. Please try again.'),
  });

  const handlePublish = () => {
    const percentValue = Number(percentage);
    const divisorValue = Number(calculationDivisor);
    if (!Number.isFinite(percentValue) || percentValue < 0 || percentValue > 100) {
      setError('Enter a percentage between 0 and 100.');
      return;
    }
    if (!Number.isFinite(divisorValue) || divisorValue <= 0) {
      setError('Enter a calculation divisor greater than 0.');
      return;
    }
    publishMutation.mutate({ percentage: percentValue, calculationDivisor: divisorValue });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Social Security Contribution Levy (SSCL)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applies only to Registration Fee, IMC Professional Handling Fee and Document Processing Fee. Publishing a new rate
          affects new calculations only — every historical payment and invoice keeps the rate that was active when it was
          created.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The percentage below is for display/reference only. The actual tax is calculated as: grossAmount = Student Amount
          ÷ Calculation Divisor, then SSCL = grossAmount − Student Amount.
        </Typography>

        {ssclQuery.data?.current && (
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            <Typography variant="h5">Current rate: {Number(ssclQuery.data.current.percentage).toFixed(2)}%</Typography>
            <Typography variant="h5">
              Divisor: {Number(ssclQuery.data.current.calculationDivisor).toFixed(4)}
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {canManage && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="SSCL Percentage (display)"
              type="number"
              size="small"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <TextField
              label="SSCL Calculation Divisor"
              type="number"
              size="small"
              value={calculationDivisor}
              onChange={(e) => setCalculationDivisor(e.target.value)}
              helperText="e.g. 0.975"
              inputProps={{ min: 0.0001, step: 0.0001 }}
            />
            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={publishMutation.isPending || !percentage || !calculationDivisor}
            >
              Publish new rate
            </Button>
          </Stack>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Percentage</TableCell>
              <TableCell>Calculation Divisor</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Published By</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(ssclQuery.data?.history ?? []).map((rate) => (
              <TableRow key={rate.id}>
                <TableCell>{Number(rate.percentage).toFixed(2)}%</TableCell>
                <TableCell>{Number(rate.calculationDivisor).toFixed(4)}</TableCell>
                <TableCell>{formatDateTime(rate.createdAt)}</TableCell>
                <TableCell>{rate.createdBy ? `${rate.createdBy.firstName} ${rate.createdBy.lastName}` : '—'}</TableCell>
                <TableCell>
                  {rate.isActive ? <Chip size="small" label="Active" color="success" /> : <Chip size="small" label="Superseded" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          USD Invoice Bank Charges
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Added to a USD Invoice's TOTAL (Subtotal + Bank Charges). Never shown or added on an LKR Invoice. Publishing a new
          amount affects new invoices only — every already-generated invoice keeps the amount that was active when it was
          created.
        </Typography>

        {bankChargeQuery.data?.current && (
          <Typography variant="h5" sx={{ mb: 2 }}>
            Current Bank Charges: $ {Number(bankChargeQuery.data.current.amountUsd).toFixed(2)}
          </Typography>
        )}

        {bankChargeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {bankChargeError}
          </Alert>
        )}

        {canManage && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="USD Invoice Bank Charges"
              type="number"
              size="small"
              value={bankChargeAmount}
              onChange={(e) => setBankChargeAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <Button
              variant="contained"
              onClick={handlePublishBankCharge}
              disabled={publishBankChargeMutation.isPending || !bankChargeAmount}
            >
              Publish new amount
            </Button>
          </Stack>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Amount (USD)</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Published By</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(bankChargeQuery.data?.history ?? []).map((charge) => (
              <TableRow key={charge.id}>
                <TableCell>$ {Number(charge.amountUsd).toFixed(2)}</TableCell>
                <TableCell>{formatDateTime(charge.createdAt)}</TableCell>
                <TableCell>{charge.createdBy ? `${charge.createdBy.firstName} ${charge.createdBy.lastName}` : '—'}</TableCell>
                <TableCell>
                  {charge.isActive ? <Chip size="small" label="Active" color="success" /> : <Chip size="small" label="Superseded" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          USD/LKR Exchange Rate History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Automatic</strong> rates are the Nations Trust Bank Telegraphic Transfers Selling Rate, received from
          the external exchange-rate fetcher on its own schedule (not triggerable from here); the latest one is the
          current rate for new calculations. <strong>Manual</strong> rates are published by Super Admin or Finance from a
          student's Payments section (Edit Exchange Rate), may be backdated, and apply only where someone selects them.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Rate (Rs.)</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Effective date</TableCell>
                <TableCell>Received / published</TableCell>
                <TableCell>Published by</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rateHistoryQuery.data ?? []).map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>{formatRate(rate.rate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={rate.origin === 'MANUAL' ? 'secondary' : 'primary'}
                      label={rate.origin === 'MANUAL' ? 'Manual' : 'Automatic'}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {rate.origin === 'MANUAL' ? 'IMC Finance' : `${rate.source} — TT Selling`}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(rate.sourcePublishedAt)}</TableCell>
                  <TableCell>{formatDateTime(rate.receivedAt)}</TableCell>
                  <TableCell>
                    {rate.origin === 'MANUAL'
                      ? rate.publishedBy
                        ? `${rate.publishedBy.firstName} ${rate.publishedBy.lastName}`
                        : '—'
                      : 'Automatic fetcher'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>{rate.reason ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Fetch Log
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Attempted</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(fetchLogQuery.data ?? []).map((attempt) => (
              <TableRow key={attempt.id}>
                <TableCell>
                  <Chip size="small" label={attempt.status} color={attempt.status === 'SUCCESS' ? 'success' : 'error'} />
                </TableCell>
                <TableCell>{attempt.rate != null ? Number(attempt.rate).toFixed(2) : '—'}</TableCell>
                <TableCell>{formatDateTime(attempt.attemptedAt)}</TableCell>
                <TableCell>{attempt.message ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
